// src/reports/PickupsVsGiftCardsReport.jsx — Reporting → Store Reports → Backend Reporting → Pickups vs Gift Cards
// (Eric, 2026-10-18). Replaces the Grafana "Order Pickups vs TeamPortalOrder Gift Card Usage" dashboard, from OpsHub's own
// pickup records and Lightspeed gift card redemptions. Needs the Extra Permission reports.backend.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Banner, Button, Tabs, Spinner, Box, Tooltip, Select } from '@shopify/polaris';
import { LineChart } from '@shopify/polaris-viz';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { withColors } from './chartPalette';
import ReportGuide from './ReportGuide';
import { ReportDateRange, ReportPicker, isoDaysAgo, todayIso, downloadCsv, paneHeightToFitScroller } from '../Reporting';

const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtP = n => (n === null || n === undefined) ? '—' : Number(n).toFixed(1) + '%';
const B = ({ children }) => <b>{children}</b>;

const CARDS = [
    { value: 'current', label: "This year's cards (Team Portal Cards)" },
    { value: 'earlier', label: 'Earlier cards (TeamPortalOrder, to Jan 2026)' },
    { value: 'both', label: 'Both' },
];
const WINDOWS = [
    { value: 'same_day', label: 'Same day' },
    { value: '3', label: 'Within 3 days' },
    { value: '7', label: 'Within 7 days' },
    { value: '30', label: 'Within 30 days' },
];
const TABS = [['stores', 'By store'], ['daily', 'Day by day'], ['matches', 'Matched customers'], ['trend', 'Daily trend']];
const GUIDE = {
    summary: 'For each corporate store: how many orders were picked up, how many Team Portal gift cards were spent at the register, and how many of those pickup customers were the ones spending a card.',
    sections: [
        { heading: 'What it is for', items: [
            'Compare order pickups with Team Portal gift card use, store by store and day by day, to see how much a pickup leads to a card being spent in the store.',
            'The ratio shows pickups per card used (a ratio of 7 means seven pickups for every card). The difference is pickups minus cards.',
            'The matched numbers go further than counts: they check whether the person who picked up an order is the person who spent a card.',
        ] },
        { heading: 'Where the numbers come from', items: [
            <><B>Pickups</B>: orders marked picked up at one of our own (corporate) stores, in OpsHub (the same pickups as the Store Fulfillments report). An order counts once per store and day. Franchise stores are not included, because their gift card use is not in our register system.</>,
            <><B>Gift cards used</B>: Team Portal gift cards spent at that store's register in Lightspeed (our point-of-sale system). The Show selector picks this year's "Team Portal Cards" ($10 cards, the default), the earlier "TeamPortalOrder" cards (used until January 2026) or both.</>,
            'Both are refreshed nightly (pickups are live in OpsHub; Lightspeed sales load at 1:15 AM), so gift cards are complete through last night, not today.',
            'Dates are the store\'s own local date.',
        ] },
        { heading: 'The boxes', items: [
            <><B>Total order pickups</B> and <B>Total gift cards used</B>: the totals for the dates and stores in view, with the dollars the cards paid. <B>Total of those transactions</B>: every register sale a card was used on in the dates, tax included, so you see what customers spent in total (a sale paid with two cards counts once) and what share the cards covered. It does not change with the match window. <B>Matched pickup transactions</B>: only the sales where a pickup customer spent a card, so it does change with the match window. <B>Pickup:card ratio</B> and <B>Difference</B> compare pickups with cards.</>,
            <><B>Pickups with a card</B>: pickups where the same customer spent a card at the same store, on the pickup day or within the match window you pick (same day, 3, 7 or 30 days). <B>Cards from pickup customers</B>: card uses by a customer who picked up an order at that store on that day or up to the window before.</>,
        ] },
        { heading: 'How customers are matched', items: [
            "The pickup order's customer email is matched to the email of the customer on the register sale that used the card. Both must be the same email at the same store.",
            'Matching misses people: some register sales have no customer email, and a card can be spent by someone other than the person who picked up (for example another parent). So matched counts are a floor, and the plain counts are the complete picture.',
            'The Matched customers tab lists each matched pickup with the card use that matched it.',
        ] },
        { heading: 'How to use it', items: [
            'Pick dates (the last 120 days to start), stores (blank = all corporate stores), the gift cards and the match window, then press Run Report. The address in your browser holds your choices.',
            'Tabs: by store, day by day with the difference, the matched customers, and a daily trend chart of pickups against cards.',
            'Click a column title to sort; hover for its meaning; Export CSV saves the table you are viewing.',
        ] },
    ],
};
const COL_HELP = {
    pickups: 'Orders marked picked up at this corporate store (one per order per day).',
    cards: 'Team Portal gift cards spent at this store\'s register.',
    value: 'Dollars paid with those cards.',
    ratio: 'Pickups ÷ cards used. 7 means seven pickups for every card used.',
    difference: 'Pickups minus cards used.',
    pickups_matched: 'Pickups where the same customer (by email) spent a card at the same store, on the pickup day or within the match window.',
    pickups_matched_pct: 'Pickups with a card ÷ pickups.',
    cards_matched: 'Card uses by a customer who picked up an order at that store that day or in the match window before.',
    cards_matched_pct: 'Cards from pickup customers ÷ cards used.',
    days_after: 'Days from the pickup to the card use (0 = the same day).',
    txns: 'Register sales where a card was used (a sale paid with two cards counts once).',
    txn_total: 'The total of those sales: everything the customer paid, tax included, not just the part paid by card.',
    card_share_pct: 'Card dollars ÷ the total of those sales: how much of the bill the cards paid.',
    matched_txns: 'The register sales where a pickup customer spent a card (within the match window). A sale shared by two pickups counts once.',
    matched_txn_total: 'The total of those matched sales, tax included: what the pickup customers spent in all.',
    sale_total: 'The whole register sale the card was used on, tax included, not just the card part.',
};

function SortTable({ cols, rows, totalRow = null, note = null, fit = true }) {
    const [sort, setSort] = useState({ key: null, dir: 'desc' });
    const wrapRef = useRef(null);
    const [paneH, setPaneH] = useState(null);
    useLayoutEffect(() => {
        if (!fit) return undefined;
        const measure = () => { const el = wrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure();
        const t = setTimeout(measure, 300);
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, [fit, rows, paneH]);
    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        const k = sort.key, d = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a[k], y = b[k];
            if (x === y) return 0;
            if (x === null || x === undefined || x === '') return 1;
            if (y === null || y === undefined || y === '') return -1;
            return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d;
        });
    }, [rows, sort]);
    const cell = (r, k, kind) => {
        const v = r[k];
        if (kind === 'n') return fmtN(v);
        if (kind === 'u') return fmtUSD(v);
        if (kind === 'p') return fmtP(v);
        if (kind === 'x') return v === null || v === undefined ? '—' : Number(v).toFixed(2);
        if (kind === 'd') return v === null || v === undefined ? '—' : (v > 0 ? '+' : '') + fmtN(v);
        if (kind === 'l') return v ? <a href={v} target="_blank" rel="noreferrer">View in Lightspeed</a> : '';
        if (kind === 'o') return r.order_id ? <Link to={`/store/soccerpost/orders/${r.order_id}`}>{v || r.order_id}</Link> : (v ?? '');
        return v ?? '';
    };
    const left = kind => ['s', 'l', 'o'].includes(kind);
    const td = (kind, extra = {}) => ({ textAlign: left(kind) ? 'left' : 'right', padding: '5px 8px', borderBottom: '1px solid var(--p-color-border-subdued, #eee)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...extra });
    return (
        <BlockStack gap="200">
            {note && <Text as="p" variant="bodySm" tone="subdued">{note}</Text>}
            <div ref={wrapRef} style={{ overflow: 'auto', maxHeight: fit ? (paneH || undefined) : 460 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--p-color-bg-surface, #fff)', zIndex: 1 }}>
                        <tr>{cols.map(([k, label, kind]) => (
                            <th key={k} onClick={() => setSort(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
                                style={{ textAlign: left(kind) ? 'left' : 'right', padding: '6px 8px', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid var(--p-color-border, #ddd)' }}>
                                <Tooltip content={COL_HELP[k] || label}><span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span></Tooltip>{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>))}</tr>
                    </thead>
                    <tbody>
                        {sorted.map((r, i) => (<tr key={i}>{cols.map(([k, , kind]) => <td key={k} style={td(kind)}>{cell(r, k, kind)}</td>)}</tr>))}
                        {totalRow && <tr>{cols.map(([k, , kind]) => <td key={k} style={td(kind, { fontWeight: 600, borderTop: '2px solid var(--p-color-border, #ccc)' })}>{cell(totalRow, k, kind)}</td>)}</tr>}
                    </tbody>
                </table>
                {rows.length === 0 && <Box padding="400"><Text as="p" tone="subdued" alignment="center">Nothing in these dates.</Text></Box>}
            </div>
        </BlockStack>
    );
}

function Stat({ label, value, sub, hint }) {
    const body = (
        <Box padding="400" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
                <Text as="p" variant="heading2xl">{value}</Text>
                {sub && <Text as="p" variant="bodySm" tone="subdued">{sub}</Text>}
            </BlockStack>
        </Box>);
    return hint ? <Tooltip content={hint}>{body}</Tooltip> : body;
}

export default function PickupsVsGiftCardsReport() {
    const fetchAuth = useAuthFetch();
    const [sp, setSp] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(sp.get('from') || isoDaysAgo(119));
    const [dateTo, setDateTo] = useState(sp.get('to') || todayIso());
    const [stores, setStores] = useState(sp.getAll('stores'));
    const [cards, setCards] = useState(sp.get('cards') || 'current');
    const [windowSel, setWindowSel] = useState(sp.get('window') || 'same_day');
    const [tab, setTabState] = useState(Math.max(0, TABS.findIndex(t => t[0] === sp.get('tab'))));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);

    const writeUrl = useCallback((f, tabIdx) => {
        setSp(prev => {
            const n = new URLSearchParams(prev);
            ['from', 'to', 'stores', 'cards', 'window', 'tab'].forEach(k => n.delete(k));
            n.set('from', f.dateFrom); n.set('to', f.dateTo);
            f.stores.forEach(v => n.append('stores', v));
            if (f.cards !== 'current') n.set('cards', f.cards);
            if (f.windowSel !== 'same_day') n.set('window', f.windowSel);
            if (tabIdx > 0) n.set('tab', TABS[tabIdx][0]);
            return n;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSp]);
    const setTab = (i) => { setTabState(i); writeUrl({ dateFrom, dateTo, stores, cards, windowSel }, i); };

    const run = useCallback(async () => {
        setBusy(true); setErr(null);
        writeUrl({ dateFrom, dateTo, stores, cards, windowSel }, tab);
        try {
            const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, cards, window: windowSel });
            stores.forEach(v => p.append('stores', v));
            const r = await fetchAuth(`/api/reports/backend/pickups-vs-gift-cards?${p}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
    }, [fetchAuth, dateFrom, dateTo, stores, cards, windowSel, tab, writeUrl]);
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const storeOptions = useMemo(() => (data?.store_options || []).map(v => ({ value: v, label: v })), [data]);
    const trend = useMemo(() => {
        if (!data) return [];
        const days = [];
        for (let d = new Date(`${data.date_from}T00:00:00Z`); d <= new Date(`${data.date_to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) days.push(d.toISOString().slice(0, 10));
        const p = {}, c = {};
        data.daily.forEach(r => { p[r.date] = (p[r.date] || 0) + r.pickups; c[r.date] = (c[r.date] || 0) + r.cards; });
        return withColors([
            { name: 'Order pickups', data: days.map(d => ({ key: d, value: p[d] || 0 })) },
            { name: 'Gift cards used', data: days.map(d => ({ key: d, value: c[d] || 0 })) },
        ]);
    }, [data]);

    const S = data?.summary;
    const key = TABS[tab][0];
    const win = WINDOWS.find(w => w.value === windowSel)?.label.toLowerCase() || 'same day';
    const COLS = {
        stores: [['store', 'Store', 's'], ['pickups', 'Order pickups', 'n'], ['cards', 'Gift cards used', 'n'], ['ratio', 'Ratio (pickups:cards)', 'x'], ['difference', 'Difference', 'd'],
            ['pickups_matched', 'Pickups with a card', 'n'], ['pickups_matched_pct', '% of pickups', 'p'], ['cards_matched', 'Cards from pickup customers', 'n'], ['cards_matched_pct', '% of cards', 'p'], ['value', 'Card value', 'u'],
            ['txns', 'Transactions with a card', 'n'], ['txn_total', 'Transaction total', 'u'], ['card_share_pct', 'Cards paid % of total', 'p'],
            ['matched_txns', 'Matched pickup transactions', 'n'], ['matched_txn_total', 'Matched transaction total', 'u']],
        daily: [['date', 'Date', 's'], ['store', 'Store', 's'], ['pickups', 'Pickups', 'n'], ['cards', 'Gift cards', 'n'], ['difference', 'Difference', 'd'], ['value', 'Card value', 'u'], ['txns', 'Transactions with a card', 'n'], ['txn_total', 'Transaction total', 'u']],
        matches: [['pickup_date', 'Pickup date', 's'], ['store', 'Store', 's'], ['order_name', 'Order', 'o'], ['customer', 'Customer', 's'], ['email', 'Email', 's'], ['card_date', 'Card used', 's'], ['days_after', 'Days after pickup', 'n'], ['amount', 'Card amount', 'u'], ['sale_total', 'Transaction total', 'u'], ['card', 'Card', 's'], ['link', 'View in Lightspeed', 'l']],
    };
    const rowsFor = { stores: data?.by_store, daily: data?.daily, matches: data?.matches };
    const totalRow = key === 'stores' && S ? { store: 'TOTAL', ...S } : null;
    const exportCsv = () => {
        if (!data || !COLS[key]) return;
        downloadCsv(`pickups-vs-gift-cards-${key}-${dateFrom}-${dateTo}.csv`, [COLS[key].map(c => c[1]), ...(rowsFor[key] || []).map(r => COLS[key].map(c => r[c[0]] ?? ''))]);
    };

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <ReportGuide id="pickups-vs-gift-cards" summary={GUIDE.summary} sections={GUIDE.sections} />
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                        <ReportPicker label="Store" options={storeOptions} value={stores} onChange={setStores} multiple searchable placeholder="All corporate stores" />
                        <div style={{ minWidth: 340 }}><Select label="Gift cards" labelInline options={CARDS} value={cards} onChange={setCards} /></div>
                        <div style={{ minWidth: 220 }}><Select label="Match" labelInline options={WINDOWS} value={windowSel} onChange={setWindowSel} /></div>
                        <Button variant="primary" onClick={run} loading={busy}>Run Report</Button>
                        <Button onClick={exportCsv} disabled={!data || !COLS[key]}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                        Pickups are orders marked picked up at our own stores; gift cards are the Team Portal cards spent at the same store's register. Customers are matched by email ({win}).
                        Lightspeed sales load nightly, so gift cards are complete through last night.
                    </Text>
                </BlockStack>
            </Card>

            {busy && !data && <InlineStack align="center"><Spinner /></InlineStack>}
            {S && (
                <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="200">
                    <Stat label="Total order pickups" value={fmtN(S.pickups)} hint="Orders marked picked up at corporate stores in these dates (one per order per day)." />
                    <Stat label="Total gift cards used" value={fmtN(S.cards)} sub={`${fmtUSD(S.value)} paid by cards`} hint="Team Portal gift cards spent at the registers of those stores, and the dollars they paid." />
                    <Stat label="Total of those transactions" value={fmtUSD(S.txn_total)} sub={`${fmtN(S.txns)} sales · cards paid ${fmtP(S.card_share_pct)}`} hint="The full register sales a card was used on, tax included: what the customers spent in total, not just the part the cards covered." />
                    <Stat label="Pickup:card ratio" value={S.ratio === null ? '—' : S.ratio.toFixed(2)} hint="Pickups ÷ cards used." />
                    <Stat label="Difference (pickups − cards)" value={(S.difference > 0 ? '+' : '') + fmtN(S.difference)} hint="Pickups minus cards used." />
                    <Stat label="Pickups with a card" value={fmtN(S.pickups_matched)} sub={`${fmtP(S.pickups_matched_pct)} of pickups (${win})`} hint="Pickups where the same customer (by email) spent a card at the same store, on the pickup day or within the match window." />
                    <Stat label="Matched pickup transactions" value={fmtUSD(S.matched_txn_total)} sub={`${fmtN(S.matched_txns)} sales · cards paid ${fmtP(S.matched_card_share_pct)}`} hint="The register sales where a pickup customer spent a card (within the match window): what those customers spent in total, tax included. This is the total that changes with the Match setting; the Total of those transactions box does not." />
                    <Stat label="Cards from pickup customers" value={fmtN(S.cards_matched)} sub={`${fmtP(S.cards_matched_pct)} of cards used`} hint="Card uses by a customer who picked up an order at that store that day or within the match window before." />
                </InlineGrid>
            )}

            {data && (
                <Card padding="0">
                    <Tabs tabs={TABS.map(([id, content]) => ({ id, content }))} selected={tab} onSelect={setTab} />
                    <Box padding="300" paddingBlockEnd="0">
                        {COLS[key] && (
                            <SortTable key={key} cols={COLS[key]} rows={rowsFor[key] || []} totalRow={totalRow}
                                note={key === 'matches' && (data.matches || []).length >= data.match_limit ? `Showing the latest ${fmtN(data.match_limit)} — narrow the dates or stores.` : null} />
                        )}
                        {key === 'trend' && <div style={{ height: 420 }}><LineChart data={trend} /></div>}
                    </Box>
                </Card>
            )}
        </BlockStack>
    );
}
